import { useEffect, useId, useRef } from "react";
import mermaid from "mermaid";

mermaid.initialize({ startOnLoad: false, theme: "default" });

interface MermaidDiagramProps {
  id?: string;
  chart: string;
  className?: string;
}

export function Mermaid({ chart, className }: MermaidDiagramProps) {
  const baseRenderId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const spinnerRef = useRef<HTMLDivElement>(null);

  // Incremented on every effect call. Allows each call to detect whether a
  // newer call has superseded it (e.g. StrictMode double-invoke or rapid chart
  // changes) and discard stale results without cancelling mermaid mid-render.
  const renderCallRef = useRef(0);

  useEffect(() => {
    if (!containerRef.current || !spinnerRef.current) return;
    const container = containerRef.current;
    const spinner = spinnerRef.current;
    const callId = ++renderCallRef.current;
    // Append callId so each concurrent render gets a unique mermaid element ID.
    const renderId = `${baseRenderId}-${callId}`;

    // Show spinner via DOM directly to avoid a React re-render that would clear
    // container.innerHTML.
    spinner.style.display = "flex";
    container.innerHTML = "";

    mermaid
      .render(renderId, chart)
      .then(({ svg, bindFunctions }) => {
        if (callId !== renderCallRef.current) return;
        spinner.style.display = "none";
        container.innerHTML = svg;
        // Wire up interactivity (click handlers etc.) declared in the diagram.
        bindFunctions?.(container);
      })
      .catch((err: unknown) => {
        if (callId !== renderCallRef.current) return;
        spinner.style.display = "none";
        container.innerHTML = `<pre class="text-sm text-red-700">${err instanceof Error ? err.message : String(err)}</pre>`;
      });
  }, [chart, baseRenderId]);

  return (
    <div className={className}>
      <div ref={spinnerRef} className="w-full flex justify-center py-6">
        <div className="animate-spin rounded-full border-4 border-gray-300 border-t-gray-900 h-10 w-10" />
      </div>
      <div
        ref={containerRef}
        className="w-full flex justify-center [&_svg]:w-full [&_svg]:h-auto"
      />
    </div>
  );
}
